import { I18n } from '@lingui/core';
import { fromPath } from '@lingui/detect-locale';

import { defaultLocale, locales } from '../constants/locales';
import { getPathnamePrefix } from './getPathnamePrefix';

export const isLocaleValid = (locale: string) => Object.keys(locales).includes(locale);

/**
 * Rebuilds a pathname for a target locale by swapping (or dropping) the leading
 * locale segment. The default locale has no prefix, so switching to it removes
 * an existing `/ru` segment; switching to a non-default locale adds/replaces it.
 *
 * Used by the language switcher: we construct the URL explicitly and hard-reload
 * instead of `navigate({ params: { locale } })`, because setting an optional
 * `{-$locale}` param to `undefined` does not reliably drop the existing prefix
 * when switching to the default locale — leaving `/ru/...` in the URL, which the
 * server then resolves back to Russian.
 */
export const buildLocaleSwitchPath = (pathname: string, newLocale: string): string => {
  const prefix = getPathnamePrefix(newLocale); // undefined for the default locale
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] && isLocaleValid(segments[0])) {
    segments.shift();
  }
  const next = prefix ? [prefix, ...segments] : segments;
  return '/' + next.join('/');
};

/**
 * We do a dynamic import of just the catalog that we need
 * @param locale any locale string
 */
export async function dynamicActivate(i18n: I18n, locale: string) {
  const { messages } = await import(`../locales/${locale}/messages.po`);
  i18n.loadAndActivate({ locale, messages });
}

export const getLocaleFromPath = (url?: Partial<Location>) => {
  const queryLocale = fromPath(0, url);

  if (isLocaleValid(queryLocale)) {
    return queryLocale;
  }

  return defaultLocale;
};
